import { HttpErrorResponse } from '@angular/common/http';
import {
  computed,
  Directive,
  inject,
  input,
  signal,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { finalize, timeout, TimeoutError } from 'rxjs';
import { BackendService, DialogService } from '@core/services';
import { BaseComponent } from '@shared/classes';
import { GenericModalComponent } from '@shared/components/modals/generic-modal/generic-modal.component';
import { OptinsModalComponent } from '@shared/components/modals/optins-modal/optins-modal.component';
import { CampaignPhase, OfferType, Pages } from '@shared/constants';
import { ChallengeBaseDTO, SelectErrorCode } from '@shared/dto';
import { AnyChallenge, BaseDialog, EmitterKeys } from '@shared/models';
import { getErrorCode } from '@shared/utils';

@Directive()
export abstract class SelectionButtonBaseClass extends BaseComponent {
  challenge = input.required<AnyChallenge>();

  isBisCampaign = input<boolean>(false);
  isEventChallenge = input<boolean>(false);
  phase = input<CampaignPhase>(CampaignPhase.CURRENT);

  maxReward = computed(() => {
    const challenge = this.challenge();
    if (challenge.type === OfferType.STEP) {
      return challenge.maxPotentialReward;
    } else if (challenge.type === OfferType.FREQUENCY) {
      return challenge.rewardRatio;
    }
    return challenge.reward;
  });

  public selectionInProgress = signal<boolean>(false);
  public blockSelectionButton = signal<boolean>(false);
  public maxChallengesAvailable: number =
    this.environment.maxSelectedChallenges;

  private readonly router = inject(Router);
  private readonly SELECTION_TIMEOUT_MS = 10000;
  private readonly dialog: DialogService = inject(DialogService);
  private readonly backendService: BackendService = inject(BackendService);

  selectChallenge(
    challenge: AnyChallenge,
    phase: CampaignPhase,
    isBisCampaign: boolean,
    isEventChallenge: boolean = false
  ): void {
    if (
      challenge.isOutOfStock() ||
      this.selectionInProgress() ||
      this.blockSelectionButton()
    ) {
      return;
    }

    this.selectionInProgress.set(true);

    this.taggingService.emitTagForKey(
      EmitterKeys.CHALLENGE_SELECT,
      challenge.getChallengeName()
    );

    // Standard flow (non-bis, non-event) caps selection client-side to skip a
    // network round-trip we already know would return MAX_SELECTION_REACHED.
    const isStandardFlow = !isBisCampaign && !isEventChallenge;
    const nbSelectedChallenges =
      phase === CampaignPhase.FUTURE
        ? this.dataService.selectedFutureChallenges
        : this.dataService.selectedCurrentChallenges;

    if (
      isStandardFlow &&
      nbSelectedChallenges() >= this.maxChallengesAvailable
    ) {
      this.openAllChallengesSelectedErrorDialog();
      this.selectionInProgress.set(false);
      return;
    }

    this.backendService
      .selectChallenge(challenge.campaignId, challenge.offerId)
      .pipe(
        timeout(this.SELECTION_TIMEOUT_MS),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.selectionInProgress.set(false))
      )
      .subscribe({
        next: (data: ChallengeBaseDTO) => {
          const challenges = this.getChallengesByContext(
            phase,
            isBisCampaign,
            isEventChallenge
          );

          this.updateSelectedChallenges(challenges, data, challenge);

          if (isStandardFlow) {
            this.openSelectionMilestoneDialog(nbSelectedChallenges());
          }
        },
        error: (error: HttpErrorResponse | TimeoutError) =>
          this.handleErrors(
            error,
            phase,
            isBisCampaign,
            isEventChallenge,
            challenge
          ),
      });
  }

  private handleErrors(
    error: HttpErrorResponse | TimeoutError,
    phase: CampaignPhase,
    isBisCampaign: boolean,
    isEventChallenge: boolean,
    challenge: AnyChallenge
  ): void {
    if (error instanceof TimeoutError) {
      this.openTimeoutErrorDialog();
      return;
    }

    switch (getErrorCode(error)) {
      case SelectErrorCode.ACCOUNT_ISSUE:
        this.openAccountIssueDialog();
        break;
      case SelectErrorCode.MAX_SELECTION_REACHED:
        this.openAllChallengesSelectedErrorDialog();
        break;
      case SelectErrorCode.OPTIN_REQUIRED:
        this.openOptinsDialog(challenge);
        break;
      case SelectErrorCode.AUTH_REQUIRED:
        this.openRedirectionWarningDialog(challenge);
        break;
      case SelectErrorCode.OUT_OF_STOCK:
        this.handleOutOfStockIssue(
          phase,
          isBisCampaign,
          isEventChallenge,
          challenge
        );
        break;
      case SelectErrorCode.ALREADY_SELECTED:
        this.updateSelectedChallenges(
          this.getChallengesByContext(phase, isBisCampaign, isEventChallenge),
          { selected_date: new Date().toISOString() },
          challenge
        );
        break;
      case SelectErrorCode.MAX_ATTEMPTS_REACHED:
        this.openTimeoutErrorDialog();
        break;
      // The page is still usable, only this action is invalid: inform in place.
      case SelectErrorCode.CHALLENGE_NOT_SELECTABLE:
      case SelectErrorCode.CAMPAIGN_OVER:
        this.openChallengeUnavailableDialog();
        break;
      // The page data is stale/invalid: send the user somewhere they can recover.
      case SelectErrorCode.CHALLENGES_NOT_FOUND:
      case SelectErrorCode.CAMPAIGN_NOT_FOUND:
        this.redirectToFallbackPage();
        break;
      default:
        // null (missing / unrecognised error_code) or any unmapped code
        this.openGenericErrorDialog();
        break;
    }
  }

  private openAccountIssueDialog(): void {
    this.blockSelectionButton.set(true);
    const config = this.loyaltyChallengeConfig.components.modals.selectionIssue;

    if (config) {
      this.openSelectionIssue('selection_issue_dialog', config);
      return;
    }

    this.openGenericErrorDialog();
  }

  private openOptinsDialog(challenge: AnyChallenge): void {
    if (!this.loyaltyChallengeConfig.components.modals.optins) {
      this.openGenericErrorDialog();
      return;
    }

    this.dialog.open(OptinsModalComponent, {
      data: challenge,
    });
  }

  private openRedirectionWarningDialog(challenge: AnyChallenge): void {
    const config =
      this.loyaltyChallengeConfig.components.modals.redirectionWarning;

    if (!config) {
      this.openGenericErrorDialog();
      return;
    }

    this.dialog.open(GenericModalComponent, {
      data: challenge,
      redirect: true,
      dataCy: 'redirection_warning_dialog',
      config,
    });
  }

  private handleOutOfStockIssue(
    phase: CampaignPhase,
    isBisCampaign: boolean,
    isEventChallenge: boolean,
    challenge: AnyChallenge
  ): void {
    const config =
      this.loyaltyChallengeConfig.components.modals.outOfStockIssue;
    this.openSelectionIssue('out_of_stock_issue_dialog', config);

    const challenges = this.getChallengesByContext(
      phase,
      isBisCampaign,
      isEventChallenge
    );

    challenges.set(
      challenges().filter((c: AnyChallenge) => c.offerId !== challenge.offerId)
    );
  }

  private getChallengesByContext(
    phase: CampaignPhase,
    isBisCampaign: boolean,
    isEventChallenge: boolean
  ): WritableSignal<AnyChallenge[]> {
    if (isEventChallenge) {
      return this.dataService.currentEventData;
    }

    if (isBisCampaign) {
      return phase === CampaignPhase.FUTURE
        ? this.dataService.futureBisChallenges
        : this.dataService.currentBisChallenges;
    }

    return phase === CampaignPhase.FUTURE
      ? this.dataService.futureChallenges
      : this.dataService.currentChallenges;
  }

  /**
   * Redirect to the login page (or the sorry page when there is none) when the
   * page is showing stale/invalid data and selection can no longer be trusted.
   */
  private redirectToFallbackPage(): void {
    const { availablePages } = this.dataService.config().pages;
    const fallback = availablePages.includes(Pages.LOGIN)
      ? Pages.LOGIN
      : Pages.SORRY;

    this.router.navigate([fallback], {
      queryParamsHandling: 'preserve',
    });
  }

  private openGenericErrorDialog(): void {
    this.dialog.open(GenericModalComponent, {
      dataCy: 'unknown_error_dialog',
      config: this.loyaltyChallengeConfig.components.modals.genericError,
    });
  }

  private openChallengeUnavailableDialog(): void {
    this.dialog.open(GenericModalComponent, {
      dataCy: 'challenge_unavailable_dialog',
      config:
        this.loyaltyChallengeConfig.components.modals.challengeUnavailable,
    });
  }

  private openTimeoutErrorDialog(): void {
    this.dialog.open(GenericModalComponent, {
      dataCy: 'timeout_error_dialog',
      config: this.loyaltyChallengeConfig.components.modals.timeoutError,
    });
  }

  private updateSelectedChallenges(
    challenges: WritableSignal<AnyChallenge[]>,
    selectedChallenge: Pick<ChallengeBaseDTO, 'selected_date'>,
    challenge: AnyChallenge
  ): void {
    challenges.set(
      challenges().map((c: AnyChallenge) => {
        if (c.offerId === challenge.offerId) {
          // Clone the class instance to preserve methods while creating new reference
          const clone = Object.assign(
            Object.create(Object.getPrototypeOf(c)),
            c
          );
          clone.selectedDate = selectedChallenge.selected_date;
          return clone;
        }
        return c;
      })
    );
  }

  /**
   * Open the milestone dialog matching the number of selected challenges
   * (first selection, or the last allowed one).
   */
  private openSelectionMilestoneDialog(selectedCount: number): void {
    if (selectedCount === 1) {
      this.openFirstSelectionDialog();
      return;
    }

    if (selectedCount === this.maxChallengesAvailable) {
      this.openLastSelectionDialog();
    }
  }

  /**
   * Open dialog when selecting the first challenge
   */
  private openFirstSelectionDialog(): void {
    this.dialog.open(GenericModalComponent, {
      dataCy: 'first_selection_dialog',
      data: this.maxChallengesAvailable - 1,
      config: this.loyaltyChallengeConfig.components.modals.firstSelection,
    });
  }

  /**
   * Open dialog when customer has issuer with his account
   */
  private openSelectionIssue(dataCy: string, config: BaseDialog): void {
    this.dialog.open(GenericModalComponent, {
      dataCy,
      config,
    });
  }

  /**
   * Open dialog when selecting the last challenge
   */
  private openLastSelectionDialog(): void {
    this.dialog.open(GenericModalComponent, {
      dataCy: 'last_selection_dialog',
      data: this.maxChallengesAvailable,
      config: this.loyaltyChallengeConfig.components.modals.lastSelection,
    });
  }

  /**
   * Open dialog when selecting over the maximum authorized number of challenges
   */
  private openAllChallengesSelectedErrorDialog(): void {
    this.dialog.open(GenericModalComponent, {
      dataCy: 'maximum_selection_dialog',
      data: this.maxChallengesAvailable,
      config: this.loyaltyChallengeConfig.components.modals.maximumSelection,
    });
  }
}
